import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Flag } from 'lucide-react';
import { generateTrailPath } from '@/lib/generateTrailPath';
import Waypoint from './Waypoint';
import TrailWalker from './TrailWalker';

interface BoardGamePathProps {
  total: number;
  current: number;
  screenLabels: string[];
  onNavigateToStep?: (index: number) => void;
}

const BoardGamePath = ({ total, current, screenLabels, onNavigateToStep }: BoardGamePathProps) => {
  const trail = useMemo(() => generateTrailPath(total, 280, 72, 40), [total]);
  const progress = total > 1 ? current / (total - 1) : 0;

  return (
    <div
      className="relative"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total - 1}
      aria-valuenow={current}
      aria-label={`Assessment progress: step ${current + 1} of ${total}`}
    >
      <svg
        viewBox={`0 0 ${trail.viewBox.width} ${trail.viewBox.height}`}
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        {/* Background trail (dashed) */}
        <path
          d={trail.svgPath}
          fill="none"
          stroke="hsl(var(--muted) / 0.4)"
          strokeWidth={3}
          strokeDasharray="8 6"
          strokeLinecap="round"
        />

        {/* Active trail (solid, animated) */}
        <motion.path
          d={trail.svgPath}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={3}
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: progress }}
          transition={{ type: 'spring', stiffness: 60, damping: 20 }}
        />

        {/* Waypoints */}
        {trail.waypoints.map((wp, i) => (
          <Waypoint
            key={i}
            x={wp.x}
            y={wp.y}
            index={i}
            status={i < current ? 'completed' : i === current ? 'active' : 'locked'}
            label={screenLabels[i] || `Step ${i + 1}`}
            onClick={() => {
              if (i < current && onNavigateToStep) onNavigateToStep(i);
            }}
          />
        ))}

        {/* Walking human figure at current position */}
        {trail.waypoints[current] && (
          <TrailWalker
            x={trail.waypoints[current].x}
            y={trail.waypoints[current].y}
            progress={progress}
            totalSteps={total}
            currentStep={current}
          />
        )}

        {/* Flag at the end */}
        {trail.waypoints.length > 0 && (
          <g transform={`translate(${trail.waypoints[trail.waypoints.length - 1].x + 20}, ${trail.waypoints[trail.waypoints.length - 1].y - 20})`}>
            <motion.g
              animate={current === total - 1 ? { y: [0, -3, 0] } : {}}
              transition={{ duration: 1.5, repeat: current === total - 1 ? Infinity : 0 }}
            >
              <Flag
                width={14}
                height={14}
                className={current >= total - 1 ? 'text-primary' : 'text-muted-foreground/30'}
              />
            </motion.g>
          </g>
        )}
      </svg>
    </div>
  );
};

export default BoardGamePath;
